import React, { useEffect, useMemo, useState } from 'react';
import { CCard, CCardBody, CCardHeader, CCardTitle, CFormGroup, CLabel, CSelect } from '@coreui/react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import _t from 'counterpart';
import {
  getRolePermissionsConfiguration,
  IRolePermissionsConfig,
  updateRolePermissionsConfiguration,
} from '../../services/BackendService';
import { extractErrorMessage, handleFormError } from '../../helpers';
import SelectMultipleFilter from '../customers/SelectMultipleFilter';
import { ISelectOption } from '../../components/ToggleSelect';

const normalize = (permissions: Array<string>) => [...permissions].sort().join('|');

const RolePermissionsSettings = () => {
  const queryClient = useQueryClient();
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedPermissions, setSelectedPermissions] = useState<Array<ISelectOption>>([]);
  const [originalPermissions, setOriginalPermissions] = useState<Array<string>>([]);

  const rolePermissionsQuery = useQuery<IRolePermissionsConfig>(
    ['role-permissions-config'],
    getRolePermissionsConfiguration,
    {
      retry: false,
      refetchOnWindowFocus: false,
      onError: (error: unknown) => toast.error(extractErrorMessage(error)),
    }
  );

  const roleToPermissions = useMemo(() => {
    const map = new Map<string, Array<string>>();
    rolePermissionsQuery.data?.rolePermissions.forEach(({ role, permissions }) => {
      map.set(role, permissions || []);
    });
    return map;
  }, [rolePermissionsQuery.data]);

  const getRoleAssignablePermissions = (role: string) => {
    const assignablePermissionsByRole = rolePermissionsQuery.data?.assignablePermissionsByRole || {};
    const scopedPermissions = assignablePermissionsByRole[role];
    if (scopedPermissions && scopedPermissions.length > 0) {
      return scopedPermissions;
    }
    return rolePermissionsQuery.data?.availablePermissions || [];
  };

  useEffect(() => {
    const firstRole = rolePermissionsQuery.data?.rolePermissions?.[0]?.role;
    if (!firstRole) {
      return;
    }

    const nextSelectedRole = selectedRole || firstRole;
    const currentPermissions = roleToPermissions.get(nextSelectedRole) || [];
    const roleAssignablePermissions = getRoleAssignablePermissions(nextSelectedRole);
    setSelectedRole(nextSelectedRole);
    setSelectedPermissions(
      roleAssignablePermissions.map((permission) => ({
        value: permission,
        label: permission,
        enabled: currentPermissions.includes(permission),
      }))
    );
    setOriginalPermissions(currentPermissions);
  }, [rolePermissionsQuery.data, roleToPermissions, selectedRole]);

  const mutation = useMutation(
    () =>
      updateRolePermissionsConfiguration(
        selectedRole,
        selectedPermissions.filter((permission) => permission.enabled).map((permission) => permission.value)
      ),
    {
    onSuccess: async () => {
      toast.success(_t('role-permissions-settings.update-success'));
      await queryClient.invalidateQueries(['role-permissions-config']);
      setOriginalPermissions(selectedPermissions.filter((permission) => permission.enabled).map((permission) => permission.value));
    },
    onError: (error: unknown) => {
      const requestError = error as { response?: { status?: number } };
      if (requestError.response?.status !== 422) {
        handleFormError(error);
        return;
      }

      toast.error(extractErrorMessage(error));
    },
  });

  const onRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value;
    const permissions = roleToPermissions.get(role) || [];
    const roleAssignablePermissions = getRoleAssignablePermissions(role);
    setSelectedRole(role);
    setSelectedPermissions(
      roleAssignablePermissions.map((permission) => ({
        value: permission,
        label: permission,
        enabled: permissions.includes(permission),
      }))
    );
    setOriginalPermissions(permissions);
  };

  const onPermissionsChanged = (option: ISelectOption) => {
    setSelectedPermissions((prev) =>
      prev.map((permission) =>
        permission.value === option.value ? { ...permission, enabled: !permission.enabled } : permission
      )
    );
  };

  const onAllPermissionsChanged = (checked: boolean) => {
    setSelectedPermissions((prev) => prev.map((permission) => ({ ...permission, enabled: checked })));
  };

  const immutableRoles = rolePermissionsQuery.data?.immutableRoles || [];
  const isImmutableRole = immutableRoles.includes(selectedRole);
  const enabledPermissions = selectedPermissions.filter((permission) => permission.enabled).map((permission) => permission.value);
  const hasChanges = normalize(enabledPermissions) !== normalize(originalPermissions);

  return (
    <CCard className="mt-4">
      <CCardHeader>
        <CCardTitle>{_t('role-permissions-settings.title')}</CCardTitle>
        <p className="mb-0 mt-1 small">{_t('role-permissions-settings.description')}</p>
      </CCardHeader>
      <CCardBody>
        {rolePermissionsQuery.isLoading && (
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}

        {!rolePermissionsQuery.isLoading && (
          <>
            <CFormGroup>
              <CLabel htmlFor="role-selector">{_t('role-permissions-settings.role-label')}</CLabel>
              <CSelect id="role-selector" value={selectedRole} onChange={onRoleChange}>
                {(rolePermissionsQuery.data?.rolePermissions || []).map(({ role }) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </CSelect>
              {isImmutableRole && (
                <small className="text-muted d-block mt-2">
                  {_t('role-permissions-settings.immutable-role-help')}
                </small>
              )}
            </CFormGroup>

            <CFormGroup>
              <CLabel htmlFor="permissions-selector">{_t('role-permissions-settings.permissions-label')}</CLabel>
              <div id="permissions-selector">
                <SelectMultipleFilter
                  data={selectedPermissions}
                  onChange={onPermissionsChanged}
                  onAllChange={onAllPermissionsChanged}
                  allowMenuOverflow
                />
              </div>
              <small className="text-muted d-block mt-2">{_t('role-permissions-settings.permissions-help')}</small>
            </CFormGroup>

            <ButtonWithLoader
              isLoading={mutation.isLoading}
              onClick={() => mutation.mutate()}
              buttonColor="primary"
              spinnerColor="secondary"
              title={_t('action.save')}
              className="btn-md"
              showText={false}
              disabled={!hasChanges || isImmutableRole}
            />
          </>
        )}
      </CCardBody>
    </CCard>
  );
};

export default RolePermissionsSettings;
